import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Wallet, Users, Lock, ArrowLeft, HelpCircle } from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";
import { ALL_FAQ } from "../lib/faqData";
import usePageMeta from "../lib/usePageMeta";

const ICONS = {
  general: HelpCircle,
  providers: Users,
  creators: Wallet,
  security: ShieldCheck,
};

const TITLES = {
  general: "General",
  providers: "For Providers (Companies)",
  creators: "For Creators",
  security: "Security & Trust",
};

const DESCRIPTIONS = {
  general: "What CheatCraft is and how the marketplace works.",
  providers: "Everything companies need to know before placing an order.",
  creators: "Get paid, set your prices, and manage incoming orders.",
  security: "Escrow, watermarks, payments, and disputes.",
};

export default function Faq() {
  usePageMeta({
    title: "FAQ — CheatCraft",
    description: "Answers to the most common questions about CheatCraft — escrow, watermarks, payments, disputes, and how to get paid as a creator.",
    canonicalPath: "/faq",
  });
  const sectionKeys = Object.keys(ALL_FAQ);

  return (
    <div data-testid="faq-page" className="max-w-3xl mx-auto px-6 py-16">
      <Link to="/" data-testid="faq-back-home" className="text-xs text-[#a0a0b0] hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> Back to home
      </Link>

      <div className="cc-overline inline-flex items-center gap-2"><Lock size={11} /> Help center</div>
      <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-white mt-3 mb-3">
        Frequently asked <span className="text-[#00ff88]">questions</span>.
      </h1>
      <p className="text-sm text-[#a0a0b0] mb-10 max-w-xl">
        Everything you need to know before posting a job, accepting an order, or releasing escrow.
        Can&apos;t find what you&apos;re looking for? <a href="#contact" className="text-[#00ff88] hover:underline">Contact support</a>.
      </p>

      {/* Quick anchor nav */}
      <nav data-testid="faq-section-nav" className="flex flex-wrap gap-2 mb-12">
        {sectionKeys.map((key) => {
          const Icon = ICONS[key] || HelpCircle;
          return (
            <a
              key={key}
              href={`#${key}`}
              data-testid={`faq-anchor-${key}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-[#15152a] bg-[#0a0a14] text-[#a0a0b0] hover:text-[#00ff88] hover:border-[#00ff88]/30 transition-colors"
            >
              <Icon size={12} /> {TITLES[key]}
            </a>
          );
        })}
      </nav>

      <div className="space-y-14">
        {sectionKeys.map((key) => {
          const Icon = ICONS[key] || HelpCircle;
          return (
            <section key={key} id={key} data-testid={`faq-section-${key}`} className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg border border-[#15152a] bg-[#06060c] flex items-center justify-center text-[#00ff88]">
                  <Icon size={15} />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-semibold text-white">{TITLES[key]}</h2>
                  <p className="text-xs text-[#606070] mt-0.5">{DESCRIPTIONS[key]}</p>
                </div>
              </div>
              <FAQAccordion items={ALL_FAQ[key]} testIdPrefix={`faq-${key}`} />
            </section>
          );
        })}
      </div>

      <div id="contact" className="mt-16 cc-surface rounded-2xl p-7 border border-[#00ff88]/15 text-center">
        <div className="cc-overline mb-2">Still stuck?</div>
        <h3 className="text-xl text-white font-semibold mb-2">Our team replies within 24 hours.</h3>
        <p className="text-sm text-[#a0a0b0] mb-5">Whether it&apos;s a payout issue or a content dispute, we&apos;ve got your back.</p>
        <a
          href="mailto:support@cheatcraft.net"
          data-testid="faq-contact-link"
          className="cc-btn-primary inline-flex px-5 py-2.5 rounded-lg text-sm"
        >
          Contact support
        </a>
      </div>
    </div>
  );
}
